import { useCallback } from 'react';
import { useCreateAutomation, useUpdateAutomation } from '../queries/useAutomationMutations';
import { stanAnalytics, stanNotify } from '@/lib';
import type { AutomationFormData } from '../validations/automationForm';
import { useUser } from '@/queries';

interface UseAutomationFormActionsProps {
    automationId?: number;
    onSuccess?: () => void;
}

/**
 * Hook to handle automation form submission actions
 * Manages save draft and publish operations
 */
export const useAutomationFormActions = ({ automationId, onSuccess }: UseAutomationFormActionsProps) => {
    const { user } = useUser();
    const createMutation = useCreateAutomation();
    const updateMutation = useUpdateAutomation();

    const isNewAutomation = !automationId || automationId === -1;

    /**
     * Transform form data to backend API format
     */
    const transformFormDataToApi = useCallback((formData: AutomationFormData) => {
        return {
            type: 'instagram' as const,
            status: formData.status,
            data: {
                trigger: {
                    source: formData.trigger_source,
                    action: 'keyword' as const,
                    data: {
                        keywords: formData.keywords.join(','), // Convert array to comma-separated string
                    },
                    media_ids: formData.media_ids.length > 0 ? formData.media_ids : undefined,
                },
                response: {
                    type: 'message' as const,
                    data: {
                        content: formData.content,
                        buttons: formData.buttons.length > 0 ? formData.buttons : undefined,
                    },
                    comment_choices: formData.comment_choices.length > 0 ? formData.comment_choices : undefined,
                },
            },
        };
    }, []);

    /**
     * Handle save as draft
     */
    const handleSaveDraft = useCallback(
        async (formData: AutomationFormData) => {
            const apiData = transformFormDataToApi({ ...formData, status: 'draft' });

            try {
                if (isNewAutomation) {
                    await createMutation.mutateAsync(apiData);
                    stanNotify({
                        type: 'success',
                        title: 'Draft Saved',
                        text: 'Your auto-DM has been saved as a draft.',
                    });
                    stanAnalytics('automation-draft-created', {
                        meta: { user_id: user?.user_id, username: user?.username },
                    });
                } else {
                    await updateMutation.mutateAsync({ id: automationId, data: apiData });
                    stanNotify({
                        type: 'success',
                        title: 'Draft Updated',
                        text: 'Your changes have been saved.',
                    });
                    stanAnalytics('automation-draft-updated', {
                        meta: { user_id: user?.user_id, username: user?.username },
                        props: { automation_id: automationId },
                    });
                }
                onSuccess?.();
            } catch {
                stanNotify({
                    type: 'error',
                    title: 'Failed to save',
                    text: 'Please try again or contact us at friends@stanwith.me',
                });
            }
        },
        [isNewAutomation, automationId, createMutation, updateMutation, transformFormDataToApi, user, onSuccess]
    );

    /**
     * Handle publish
     */
    const handlePublish = useCallback(
        async (formData: AutomationFormData) => {
            const apiData = transformFormDataToApi({ ...formData, status: 'published' });

            try {
                if (isNewAutomation) {
                    await createMutation.mutateAsync(apiData);
                    stanNotify({
                        type: 'success',
                        title: 'Published',
                        text: 'Your auto-DM is now active.',
                    });
                    stanAnalytics('automation-published', {
                        meta: { user_id: user?.user_id, username: user?.username },
                    });
                } else {
                    await updateMutation.mutateAsync({ id: automationId, data: apiData });
                    stanNotify({
                        type: 'success',
                        title: 'Changes Published',
                        text: 'Your auto-DM has been updated and is active.',
                    });
                    stanAnalytics('automation-updated-and-published', {
                        meta: { user_id: user?.user_id, username: user?.username },
                        props: { automation_id: automationId },
                    });
                }
                onSuccess?.();
            } catch {
                stanNotify({
                    type: 'error',
                    title: 'Failed to publish',
                    text: 'Please try again or contact us at friends@stanwith.me',
                });
            }
        },
        [isNewAutomation, automationId, createMutation, updateMutation, transformFormDataToApi, user, onSuccess]
    );

    return {
        handleSaveDraft,
        handlePublish,
        isSaving: createMutation.isPending || updateMutation.isPending,
    };
};
